import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Target, ArrowLeft, Save, RotateCcw, Settings, 
  Flame, Thermometer, Snowflake, Info, TrendingUp
} from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';

const API = process.env.REACT_APP_BACKEND_URL;

const AILeadScoring = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    if (config && originalConfig) {
      setHasChanges(JSON.stringify(config) !== JSON.stringify(originalConfig));
    }
  }, [config, originalConfig]);

  const fetchConfig = async () => {
    try {
      const token = sessionStorage.getItem('auth_token');
      const response = await axios.get(`${API}/api/ai-intelligence/modules/lead_scoring`, {
        params: { token }
      });
      
      if (response.data.success) {
        setConfig(response.data.module);
        setOriginalConfig(JSON.parse(JSON.stringify(response.data.module)));
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('auth_token');
      const response = await axios.put(
        `${API}/api/ai-intelligence/modules/lead_scoring?token=${token}`,
        {
          enabled: config.enabled,
          settings: config.settings
        }
      );
      
      if (response.data.success) {
        setOriginalConfig(JSON.parse(JSON.stringify(config)));
        toast.success('Configuration saved successfully');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      const token = sessionStorage.getItem('auth_token');
      await axios.post(`${API}/api/ai-intelligence/modules/lead_scoring/reset?token=${token}`);
      toast.success('Reset to defaults');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to reset');
    }
  };

  const updateSetting = (key, value) => {
    setConfig(prev => ({
      ...prev,
      settings: { ...prev.settings, [key]: value }
    }));
  };

  const updateScoringFactor = (factor, field, value) => {
    setConfig(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        scoring_factors: {
          ...prev.settings.scoring_factors,
          [factor]: {
            ...prev.settings.scoring_factors[factor],
            [field]: value
          }
        }
      }
    }));
  };

  const updateSourceScore = (source, value) => {
    setConfig(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        source_scores: {
          ...prev.settings.source_scores,
          [source]: parseInt(value)
        }
      }
    }));
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
        </div>
      </div>
    );
  }

  const scoringFactors = config?.settings?.scoring_factors || {};
  const sourceScores = config?.settings?.source_scores || {};

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="lead-scoring-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/ai-intelligence')}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl">
              <Target className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Lead Scoring</h1>
              <p className="text-sm text-slate-500">AI-powered lead prioritization</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              Unsaved Changes
            </Badge>
          )}
          <Switch
            checked={config?.enabled}
            onCheckedChange={(checked) => setConfig(prev => ({ ...prev, enabled: checked }))}
            data-testid="module-toggle"
          />
          <span className={`text-sm font-medium ${config?.enabled ? 'text-green-600' : 'text-slate-500'}`}>
            {config?.enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      </div>

      {!config?.enabled && (
        <Alert className="bg-amber-50 border-amber-200">
          <Info className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            This module is currently disabled. Enable it to use AI lead scoring.
          </AlertDescription>
        </Alert>
      )}

      {/* Score Mode */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-5 w-5 text-orange-600" />
            Scoring Mode
          </CardTitle>
          <CardDescription>
            Choose how leads are scored
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                config?.settings?.score_mode === 'static_rules'
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => updateSetting('score_mode', 'static_rules')}
              data-testid="mode-static"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg ${
                  config?.settings?.score_mode === 'static_rules' ? 'bg-orange-500' : 'bg-slate-200'
                }`}>
                  <Settings className={`h-4 w-4 ${
                    config?.settings?.score_mode === 'static_rules' ? 'text-white' : 'text-slate-600'
                  }`} />
                </div>
                <span className="font-medium">Static Rules</span>
              </div>
              <p className="text-sm text-slate-500">
                Score based on predefined rules and weights
              </p>
            </div>
            
            <div
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                config?.settings?.score_mode === 'ai_pattern'
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => updateSetting('score_mode', 'ai_pattern')}
              data-testid="mode-ai"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg ${
                  config?.settings?.score_mode === 'ai_pattern' ? 'bg-orange-500' : 'bg-slate-200'
                }`}>
                  <TrendingUp className={`h-4 w-4 ${
                    config?.settings?.score_mode === 'ai_pattern' ? 'text-white' : 'text-slate-600'
                  }`} />
                </div>
                <span className="font-medium">AI Pattern Based</span>
              </div>
              <p className="text-sm text-slate-500">
                Score based on similarity to converted leads
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Thresholds */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Thermometer className="h-5 w-5 text-orange-600" />
            Score Thresholds
          </CardTitle>
          <CardDescription>
            Define score ranges for Hot, Warm, and Cold leads
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-3 gap-6">
            <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
              <Flame className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="font-medium text-red-700">Hot</p>
              <p className="text-sm text-red-600">≥ {config?.settings?.high_priority_threshold || 70}</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-200">
              <Thermometer className="h-8 w-8 text-amber-500 mx-auto mb-2" />
              <p className="font-medium text-amber-700">Warm</p>
              <p className="text-sm text-amber-600">{config?.settings?.medium_priority_threshold || 40} - {(config?.settings?.high_priority_threshold || 70) - 1}</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
              <Snowflake className="h-8 w-8 text-blue-500 mx-auto mb-2" />
              <p className="font-medium text-blue-700">Cold</p>
              <p className="text-sm text-blue-600">&lt; {config?.settings?.medium_priority_threshold || 40}</p>
            </div>
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <Label>High Priority Threshold (Hot)</Label>
              <span className="text-sm font-medium">{config?.settings?.high_priority_threshold || 70}</span>
            </div>
            <Slider
              value={[config?.settings?.high_priority_threshold || 70]}
              onValueChange={([value]) => updateSetting('high_priority_threshold', value)}
              max={100}
              min={50}
              step={5}
              data-testid="high-threshold"
            />
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <Label>Medium Priority Threshold (Warm)</Label>
              <span className="text-sm font-medium">{config?.settings?.medium_priority_threshold || 40}</span>
            </div>
            <Slider
              value={[config?.settings?.medium_priority_threshold || 40]}
              onValueChange={([value]) => updateSetting('medium_priority_threshold', value)}
              max={60}
              min={20}
              step={5}
              data-testid="medium-threshold"
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <Label className="font-medium">Auto-Tag High Score Leads</Label>
              <p className="text-sm text-slate-500">Automatically tag hot leads for priority</p>
            </div>
            <Switch
              checked={config?.settings?.auto_tag_high_score}
              onCheckedChange={(checked) => updateSetting('auto_tag_high_score', checked)}
              data-testid="auto-tag"
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <Label className="font-medium">Use Score in Assignment</Label>
              <p className="text-sm text-slate-500">Factor score into smart lead assignment</p>
            </div>
            <Switch
              checked={config?.settings?.use_in_assignment}
              onCheckedChange={(checked) => updateSetting('use_in_assignment', checked)}
              data-testid="use-in-assignment"
            />
          </div>
        </CardContent>
      </Card>

      {/* Scoring Factors */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="h-5 w-5 text-orange-600" />
            Scoring Factors
          </CardTitle>
          <CardDescription>
            Enable/disable and weight different scoring factors
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(scoringFactors).map(([factor, settings]) => (
            <div key={factor} className="p-4 bg-slate-50 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={settings.enabled}
                    onCheckedChange={(checked) => updateScoringFactor(factor, 'enabled', checked)}
                    data-testid={`factor-${factor}-enabled`}
                  />
                  <Label className="font-medium capitalize">{factor.replace('_', ' ')}</Label>
                </div>
                <Badge variant="outline">{settings.weight}%</Badge>
              </div>
              {settings.enabled && (
                <div>
                  <Slider
                    value={[settings.weight]}
                    onValueChange={([value]) => updateScoringFactor(factor, 'weight', value)}
                    max={50}
                    min={5}
                    step={5}
                    data-testid={`factor-${factor}-weight`}
                  />
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Source Scores */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-orange-600" />
            Source Base Scores
          </CardTitle>
          <CardDescription>
            Assign base scores by lead source
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(sourceScores).map(([source, score]) => (
              <div key={source} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Label className="flex-1 text-sm">{source}</Label>
                <Input
                  type="number"
                  value={score}
                  onChange={(e) => updateSourceScore(source, e.target.value)}
                  min={0}
                  max={50}
                  className="w-20"
                  data-testid={`source-${source}`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button
          variant="outline"
          onClick={handleReset}
          className="text-slate-600"
          data-testid="reset-btn"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset to Defaults
        </Button>
        
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="bg-orange-600 hover:bg-orange-700"
          data-testid="save-btn"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Configuration
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AILeadScoring;
